var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo } from 'react';
import { useUseCase } from "../../../hooks/useUseCase";
import { Button, Dialog, DialogActions, DialogButton, DialogContent, DialogTitle, Divider, ExpandLess, HeaderTabButtonGroup, makeStyles, Pagination, useDeclarativeLoading, } from "../../../index";
import { useTranslation } from "../../../hooks/useTranslation";
import { useAlert } from "../../../hooks/useAlert";
import { SelectImage } from "./components/SelectImage";
import { UploadImage } from "./components/UploadImage";
import { Camera } from "./components/Camera";
import { ImageInputDialogState } from "./ImageInputDialogState";
import { ChangeTabUseCase } from "./usecases/ChangeTabUseCase";
import { ChangeCategoryUseCase } from "./usecases/ChangeCategoryUseCase";
import { ChangeImageUseCase } from "./usecases/ChangeImageUseCase";
import { ChangePageUseCase } from "./usecases/ChangePageUseCase";
import { ChangeImageDataUrlUseCase } from "./usecases/ChangeImageDataUrlUserCase";
import { CloseImageInputDialogUseCase } from "./usecases/CloseImageInputDialogUseCase";
import { CONTENTS_PER_PAGE } from "./constants/select-image";
import { CloseAlertUseCase } from "./usecases/CloseAlertUseCase";
var useStyles = makeStyles(function (theme) { return ({
    container: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        padding: 0,
    },
    header: {
        position: 'relative',
        minHeight: 72,
    },
    up: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        margin: 'auto',
    },
    tab: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        margin: 'auto',
    },
    pagination: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        margin: 'auto',
    },
    main: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: "calc(100% - 72px - " + theme.shape.borderWidth.normal + "px)",
        overflow: 'auto',
    },
}); });
export var ImageInputDialog = function (props) {
    var _a;
    var tabs = props.tabs, activeTab = props.activeTab, open = props.open, isIcon = props.isIcon, onSelect = props.onSelect, onCancel = props.onCancel, others = __rest(props, ["tabs", "activeTab", "open", "isIcon", "onSelect", "onCancel"]);
    var _b = useUseCase(new ImageInputDialogState()), state = _b.state, usecase = _b.usecase;
    var classes = useStyles();
    var t = useTranslation().t;
    useEffect(function () {
        if (open) {
            usecase(new ChangeTabUseCase()).execute(activeTab);
        }
    }, [usecase, open, activeTab]);
    useEffect(function () {
        if (open && tabs.includes('select') && state.activeTab === 'select') {
            usecase(new ChangeCategoryUseCase()).execute(0);
        }
    }, [usecase, open, tabs, state.activeTab]);
    var handleClickUp = useCallback(function () {
        var _a;
        if ((_a = state.activeCategory) === null || _a === void 0 ? void 0 : _a.parentId) {
            usecase(new ChangeCategoryUseCase()).execute(state.activeCategory.parentId);
        }
    }, [usecase, state.activeCategory]);
    var handleChangeTab = useCallback(function (tab) {
        usecase(new ChangeTabUseCase()).execute(tab);
    }, [usecase]);
    var handleChangePage = useCallback(function (page) {
        var _a;
        if ((_a = state.activeCategory) === null || _a === void 0 ? void 0 : _a.id) {
            usecase(new ChangePageUseCase()).execute(state.activeCategory.id, page);
        }
    }, [usecase, state.activeCategory]);
    var handleChangeImage = useCallback(function (id) {
        usecase(new ChangeImageUseCase()).execute(id);
    }, [usecase]);
    var handleChangeCategory = useCallback(function (id) {
        usecase(new ChangeCategoryUseCase()).execute(id);
    }, [usecase]);
    var handleChangeImageDataUrl = useCallback(function (imageDataUrl) {
        usecase(new ChangeImageDataUrlUseCase()).execute(imageDataUrl);
    }, [usecase]);
    var handleSelect = useCallback(function () {
        switch (state.activeTab) {
            case 'select':
                if (state.activeImage) {
                    onSelect === null || onSelect === void 0 ? void 0 : onSelect(state.activeImage.path);
                }
                break;
            case 'upload':
            case 'camera':
                if (state.imageDataUrl) {
                    onSelect === null || onSelect === void 0 ? void 0 : onSelect(state.imageDataUrl);
                }
                break;
            default:
                break;
        }
        usecase(new CloseImageInputDialogUseCase()).execute();
    }, [state.activeTab, state.activeImage, state.imageDataUrl, usecase, onSelect]);
    var handleCancel = useCallback(function () {
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
        usecase(new CloseImageInputDialogUseCase()).execute();
    }, [onCancel, usecase]);
    var lastPage = useMemo(function () {
        return Math.ceil(state.contentsTotal / CONTENTS_PER_PAGE);
    }, [state.contentsTotal]);
    var selectButtonDisabled = useMemo(function () {
        switch (state.activeTab) {
            case 'select':
                return !state.activeImage;
            case 'upload':
                return !state.imageDataUrl;
            case 'camera':
                return !state.imageDataUrl;
            default:
                return false;
        }
    }, [state.activeTab, state.activeImage, state.imageDataUrl]);
    useDeclarativeLoading('lockLoading', state.isLoading);
    var alert = useAlert().alert;
    useEffect(function () {
        if (state.requiredAlert) {
            alert(t(state.requiredAlert.errorMessage)).then(function () {
                usecase(new CloseAlertUseCase()).execute();
            });
        }
    }, [alert, state.requiredAlert, t, usecase]);
    return (React.createElement(Dialog, __assign({ open: open, fullScreen: true }, others),
        React.createElement(DialogTitle, null, t('画像を選ぶ')),
        React.createElement(DialogContent, { className: classes.container },
            React.createElement("div", { className: classes.header },
                state.activeTab === 'select' && (React.createElement("div", { className: classes.up },
                    React.createElement(Button, { disabled: !((_a = state.activeCategory) === null || _a === void 0 ? void 0 : _a.parentId), onClick: handleClickUp },
                        React.createElement(ExpandLess, null),
                        React.createElement("span", null, t('上へ'))))),
                React.createElement("div", { className: classes.tab, style: { width: 160 * tabs.length } },
                    React.createElement(HeaderTabButtonGroup, { tabs: tabs.map(function (tab) {
                            switch (tab) {
                                case 'select':
                                    return { id: 'select', name: t('イラスト') };
                                case 'upload':
                                    return { id: 'upload', name: t('アップロード') };
                                case 'camera':
                                    return { id: 'camera', name: t('カメラ') };
                                default:
                                    throw new Error();
                            }
                        }), value: state.activeTab, onChange: handleChangeTab })),
                state.activeTab === 'select' && (React.createElement("div", { className: classes.pagination },
                    React.createElement(Pagination, { page: state.currentPage, lastPage: lastPage, onChange: handleChangePage })))),
            React.createElement(Divider, null),
            React.createElement("div", { className: classes.main },
                state.activeTab === 'select' && (React.createElement(SelectImage, { activeImage: state.activeImage, activeCategory: state.activeCategory, siblingCategories: state.siblingCategories, contents: state.contents, onChangeImage: handleChangeImage, onChangeCategory: handleChangeCategory })),
                state.activeTab === 'upload' && (React.createElement(UploadImage, { isIcon: isIcon, onChangeImageDataUrl: handleChangeImageDataUrl })),
                state.activeTab === 'camera' && (React.createElement(Camera, { isIcon: isIcon, onChangeImageDataUrl: handleChangeImageDataUrl })))),
        React.createElement(DialogActions, null,
            React.createElement(DialogButton, { onClick: handleCancel }, t('キャンセル')),
            React.createElement(DialogButton, { fill: true, disabled: selectButtonDisabled, onClick: handleSelect }, t('選ぶ')))));
};
